import React, { useCallback, useEffect, useState } from 'react';
import styles from '../tables.module.css';
import { PRISMAX_BACKEND_URL } from "../../../configs/config";

const PAYMENT_SOURCES = ['solana', 'ethereum', 'base', 'monad', 'stripe'];
const SOURCE_LABELS = {
  solana: 'Solana',
  ethereum: 'Ethereum',
  base: 'Base',
  monad: 'Monad',
  stripe: 'Stripe',
};
const TIER_LABELS = {
  '99': 'Amplifier',
  '399': 'Innovator',
};
const EXPLORER_URLS = {
  solana: 'https://solscan.io/tx/',
  ethereum: 'https://etherscan.io/tx/',
  base: 'https://basescan.org/tx/',
  monad: 'https://monad.socialscan.io/tx/'
};
const HASH_KEYS = ['transaction_hash', 'tx_hash', 'hash', 'signature'];

const VerifyPaymentRecordsTable = () => {
  const [records, setRecords] = useState(
    PAYMENT_SOURCES.map((source) => ({ payment_source: source }))
  );
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);
  const [error, setError] = useState(null);

  const fetchRecords = useCallback(async () => {
    try {
      const token = localStorage.getItem('prismax_admin_token');
      if (!token) {
        throw new Error('Missing admin token');
      }

      const response = await fetch(`${PRISMAX_BACKEND_URL}/api/verify-payment-records`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        throw new Error('Admin token expired. Please reconnect wallet.');
      }

      if (!response.ok) {
        throw new Error('Failed to fetch verify payment records');
      }

      const data = await response.json();

      if (!data.success || !Array.isArray(data.data)) {
        throw new Error('Invalid data format');
      }

      const recordBySource = new Map();
      data.data.forEach((record) => {
        if (record?.payment_source && !recordBySource.has(record.payment_source)) {
          recordBySource.set(record.payment_source, record);
        }
      });

      setRecords(
        PAYMENT_SOURCES.map((source) => recordBySource.get(source) ?? { payment_source: source })
      );
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const reconcileStripePayments = async () => {
    setReconciling(true);
    setError(null);
    try {
      const token = localStorage.getItem('prismax_admin_token');
      if (!token) {
        throw new Error('Missing admin token');
      }

      const response = await fetch(
        `${PRISMAX_BACKEND_URL}/api/admin/stripe/reconcile-payments`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        }
      );
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.msg || 'Failed to reconcile Stripe payments');
      }
      await fetchRecords();
    } catch (err) {
      setError(err.message);
    } finally {
      setReconciling(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) {
      return 'N/A';
    }
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
      return timestamp;
    }
    return date.toLocaleString();
  };

  const getSourceLabel = (source) => {
    if (!source) {
      return 'N/A';
    }
    return SOURCE_LABELS[source] || source;
  };

  const parseJson = (value) => {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (
        (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
        (trimmed.startsWith('[') && trimmed.endsWith(']'))
      ) {
        try {
          return JSON.parse(trimmed);
        } catch (err) {
          return trimmed;
        }
      }
      return trimmed;
    }

    return value;
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return '—';
    }

    if (typeof value === 'number') {
      return value.toLocaleString();
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    const numeric = Number(value);
    if (!Number.isNaN(numeric)) {
      return numeric.toLocaleString();
    }

    return String(value);
  };

  const formatKey = (key) => {
    if (TIER_LABELS[key]) {
      return TIER_LABELS[key];
    }

    if (!key) {
      return 'Unknown';
    }

    return key
      .toString()
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const formatFallback = (value) => {
    if (value === null || value === undefined) {
      return 'N/A';
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed || trimmed === '[]' || trimmed === '{}') {
        return '—';
      }
      return trimmed.length > 200 ? `${trimmed.slice(0, 200)}…` : trimmed;
    }

    try {
      const serialized = JSON.stringify(value);
      if (!serialized || serialized === '[]' || serialized === '{}') {
        return '—';
      }
      return serialized.length > 200 ? `${serialized.slice(0, 200)}…` : serialized;
    } catch (err) {
      return String(value);
    }
  };

  const summarizeTotals = (value) => {
    const parsed = parseJson(value);

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {
        display: formatFallback(parsed ?? value),
        totals: null,
      };
    }

    const entries = Object.entries(parsed);
    if (entries.length === 0) {
      return {
        display: '—',
        totals: {},
      };
    }

    const totals = {};
    const displayParts = entries.map(([key, val]) => {
      const label = formatKey(key);
      const formattedValue = formatValue(val);
      const numericVal = Number(val);
      totals[label] = Number.isFinite(numericVal) ? numericVal : formattedValue;
      return `${label}: ${formattedValue}`;
    });

    return {
      display: displayParts.join(', '),
      totals,
    };
  };

  const totalsMismatch = (onchainTotals, dbTotals) => {
    const hasOnchain = onchainTotals && Object.keys(onchainTotals).length > 0;
    const hasDb = dbTotals && Object.keys(dbTotals).length > 0;

    if (!hasOnchain && !hasDb) {
      return false;
    }

    if (!hasOnchain || !hasDb) {
      return true;
    }

    const allKeys = new Set([
      ...Object.keys(onchainTotals),
      ...Object.keys(dbTotals),
    ]);

    for (const key of allKeys) {
      const onchainValue = onchainTotals[key];
      const dbValue = dbTotals[key];
      if (onchainValue === undefined || dbValue === undefined) {
        return true;
      }
      if (onchainValue !== dbValue) {
        return true;
      }
    }

    return false;
  };

  const collectHashes = (data) => {
    if (!data) {
      return [];
    }

    if (typeof data === 'string') {
      return data
        .split(/[\s,]+/)
        .map((part) => part.trim())
        .filter((part) => part.length >= 20);
    }

    if (Array.isArray(data)) {
      return data.flatMap((item) => collectHashes(item));
    }

    if (typeof data === 'object') {
      const found = [];
      HASH_KEYS.forEach((key) => {
        if (typeof data[key] === 'string' && data[key].length >= 20) {
          found.push(data[key]);
        }
      });

      Object.values(data).forEach((value) => {
        if (value && (typeof value === 'string' || typeof value === 'object')) {
          found.push(...collectHashes(value));
        }
      });

      return found;
    }

    return [];
  };

  const getExplorerBaseUrl = (source) => {
    return EXPLORER_URLS[source] || null;
  };

  const truncateHash = (hash) => {
    if (hash.length <= 16) {
      return hash;
    }
    return `${hash.slice(0, 10)}…${hash.slice(-6)}`;
  };

  const renderTotalsCell = (summary) => {
    if (!summary.display || summary.display === 'N/A' || summary.display === '—') {
      return <span className={styles.muted}>N/A</span>;
    }
    return <span className={styles.dataText}>{summary.display}</span>;
  };

  const renderDetailsCell = (source, value) => {
    if (source !== 'stripe'){
      return
    }

    const parsed = parseJson(value);
    const reconciliation = parsed?.stripe_reconciliation;
    if (reconciliation && typeof reconciliation === 'object') {
      const exceptions = [
        ...(Array.isArray(reconciliation.anomalies) ? reconciliation.anomalies : []),
        ...(Array.isArray(reconciliation.unresolved) ? reconciliation.unresolved : []),
      ];

      if (exceptions.length === 0) {
        return <span className={styles.muted}>No exceptions</span>;
      }

      return (
        <div className={styles.detailsList}>
          {exceptions.map((item, index) => {
            const reason = formatKey(item.reason || 'unknown');
            const user = item.user_id ? `User ${item.user_id}` : 'Unknown user';
            const paymentIntentIds = Array.isArray(item.payment_intent_ids)
              ? item.payment_intent_ids
              : item.payment_intent_id
                ? [item.payment_intent_id]
                : [];
            const intent = paymentIntentIds.length > 0
              ? paymentIntentIds.map(truncateHash).join(', ')
              : 'No PaymentIntent';
            const count = item.payment_count ? ` (${item.payment_count} payments)` : '';
            return (
              <span
                key={`${item.payment_intent_id || 'exception'}-${index}`}
                className={styles.detailText}
                title={JSON.stringify(item)}
              >
                {reason}{count}: {user}, {intent}
              </span>
            );
          })}
        </div>
      );
    }

    const hashes = Array.from(new Set(collectHashes(parsed)));

    if (hashes.length > 0) {
      const explorerBase = getExplorerBaseUrl(source);
      if (explorerBase) {
        return (
          <div className={styles.detailsList}>
            {hashes.map((hash) => (
              <a
                key={hash}
                href={`${explorerBase}${hash}`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.detailLink}
              >
                {truncateHash(hash)}
              </a>
            ))}
          </div>
        );
      }

      return (
        <div className={styles.detailsList}>
          {hashes.map((hash) => (
            <span key={hash} className={styles.detailText} title={hash}>
              {truncateHash(hash)}
            </span>
          ))}
        </div>
      );
    }

    const fallback = formatFallback(parsed ?? value);
    if (!fallback || fallback === 'N/A' || fallback === '—') {
      return <span className={styles.muted}>N/A</span>;
    }

    return <span className={styles.dataText}>{fallback}</span>;
  };

  const hasStripeExceptions = (source, value) => {
    if (source !== 'stripe') {
      return false;
    }
    const parsed = parseJson(value);
    const reconciliation = parsed?.stripe_reconciliation;
    if (!reconciliation || typeof reconciliation !== 'object') {
      return false;
    }
    return (
      (Array.isArray(reconciliation.anomalies) && reconciliation.anomalies.length > 0) ||
      (Array.isArray(reconciliation.unresolved) && reconciliation.unresolved.length > 0)
    );
  };

  const renderStatusCell = (requiresReview, hasTotalsData) => {
    if (requiresReview) {
      return (
        <span className={styles.statusWarning} title="Payment reconciliation requires review">
          ⚠️
        </span>
      );
    }
    if (hasTotalsData) {
      return (
        <span className={styles.statusOk} title="Onchain and DB totals match">
          ✔
        </span>
      );
    }
    return <span className={styles.muted}>—</span>;
  };

  if (loading) {
    return <div className={styles.loading}>Loading verify payment records...</div>;
  }

  if (error) {
    return <div className={styles.error}>Error: {error}</div>;
  }

  return (
    <div className={styles.tableContainer}>
      <h2 className={styles.tableTitle}>Verify Payment Records</h2>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Payment Source</th>
              <th>Updated Time Stamp</th>
              <th>Onchain Data</th>
              <th>DB Data</th>
              <th>Status</th>
              <th>Stripe Exceptions</th>
            </tr>
          </thead>
          <tbody>
            {records.map((record) => {
              const onchainSummary = summarizeTotals(record.current_query_onchain_data);
              const dbSummary = summarizeTotals(record.current_query_db_data);
              const mismatch = totalsMismatch(onchainSummary.totals, dbSummary.totals);
              const hasTotalsData =
                (onchainSummary.totals && Object.keys(onchainSummary.totals).length > 0) ||
                (dbSummary.totals && Object.keys(dbSummary.totals).length > 0);
              const stripeExceptions = hasStripeExceptions(
                record.payment_source,
                record.current_query_off_data
              );

              return (
                <tr key={record.payment_source}>
                  <td>
                    <div className={styles.paymentSourceCell}>
                      <span>{getSourceLabel(record.payment_source)}</span>
                      {record.payment_source === 'stripe' && (
                        <button
                          type="button"
                          className={styles.rowActionButton}
                          onClick={reconcileStripePayments}
                          disabled={reconciling}
                        >
                          {reconciling ? 'Reconciling...' : 'Reconcile Stripe'}
                        </button>
                      )}
                    </div>
                  </td>
                  <td>{formatTimestamp(record.current_query_timestamp)}</td>
                  <td>{renderTotalsCell(onchainSummary)}</td>
                  <td>{renderTotalsCell(dbSummary)}</td>
                  <td>{renderStatusCell(mismatch || stripeExceptions, hasTotalsData)}</td>
                  <td>{renderDetailsCell(record.payment_source, record.current_query_off_data)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VerifyPaymentRecordsTable;
